/**
 * GBP Services editor (chunk3z-fix82).
 *
 * Matches the Core 30 reference Google Business Profile section:
 *
 *   1. GBP Services         — a FLAT chip list of every service across all
 *                             categories. Editing here (remove / add) changes
 *                             the master service list but does NOT change which
 *                             category a service belongs to.
 *   2. Services by Category — services shown as removable chips grouped under
 *                             their category, with an "add service" input per
 *                             category, plus add / delete category.
 *   3. Unassigned           — services that exist in the flat list but aren't
 *                             assigned to any category. Add them to a category
 *                             above to assign them; they don't appear on
 *                             category pages until assigned.
 *
 * The underlying source of truth stays the existing `gbpServicesText` string
 * (CATEGORY line + plain service lines) so the save path is unchanged. The
 * reserved category "Unassigned" is serialized like any other category header
 * (UNASSIGNED) and round-trips through the existing parser.
 */
import { useMemo, useState } from "react";
import { parseGbpServicesText } from "./settingsForm";

const UNASSIGNED = "Unassigned";

function serialize(map: Record<string, string[]>): string {
  const lines: string[] = [];
  for (const [category, services] of Object.entries(map)) {
    lines.push(category.toUpperCase());
    for (const s of services) lines.push(s);
    lines.push("");
  }
  return lines.join("\n").trim();
}

/** Case-insensitive membership test for a service across the whole map. */
function findServiceCategory(
  map: Record<string, string[]>,
  service: string
): string | null {
  const needle = service.trim().toLowerCase();
  for (const [cat, list] of Object.entries(map)) {
    if (list.some((s) => s.trim().toLowerCase() === needle)) return cat;
  }
  return null;
}

/**
 * chunk3z-fix158: detect the "category pasted in as a service" pattern that VAs
 * used to do — e.g. a "Teeth Whitening" service under the "Teeth Whitening
 * Service" category, or "Emergency Dentistry" under "Emergency Dental Service".
 * These shouldn't be listed as services (the category already covers them), so
 * the UI highlights them in light red.
 *
 * Rule: normalize away GBP category suffix/filler words ("service(s)",
 * "provider", "center", "clinic", "of", "and") AND collapse the dentist family
 * ("dentist"/"dentistry"/"dental" → "dent") so "Emergency Dentistry" matches
 * "Emergency Dental Service". Then the service mirrors its category when their
 * normalized core token sets are equal. "Broken Teeth" / "Severe Toothaches"
 * under "Emergency Dental Service" do NOT match (different core tokens), so they
 * stay normal — exactly the intended behavior.
 */
function normalizeForMirror(s: string): string[] {
  const drop = new Set([
    "service", "services", "provider", "providers", "center", "centre",
    "clinic", "office", "practice", "of", "and", "the", "for", "a",
  ]);
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/[\s-]+/)
    .filter(Boolean)
    // Collapse the dentist family so "dentistry"/"dental"/"dentist" all match.
    .map((t) => (/^dent(ist|istry|al)?$/.test(t) ? "dent" : t))
    // Singularize a trailing "s" so "dentures" matches "denture", "implants"
    // matches "implant", etc. (cheap stemmer — good enough for these labels).
    .map((t) => (t.length > 3 && t.endsWith("s") ? t.slice(0, -1) : t))
    .filter((t) => !drop.has(t));
}

function serviceMirrorsCategory(service: string, category: string): boolean {
  const sv = normalizeForMirror(service);
  const cat = normalizeForMirror(category);
  if (sv.length === 0 || cat.length === 0) return false;
  const catSet = new Set(cat);
  // The service "mirrors" its category when every core token of the service
  // appears in the category's core tokens (subset). This catches:
  //   "Dentures" ⊆ "Denture Care Center"      → highlight
  //   "Teeth Whitening" ⊆ "Teeth Whitening Service" → highlight
  //   "Emergency Dentistry" ⊆ "Emergency Dental Service" → highlight (dent-collapse)
  // …while leaving genuine sub-services alone because they add a token the
  // category doesn't have:
  //   "Full Dentures"  ({full,denture}) ⊄ {denture,care} → normal
  //   "Broken Teeth"   ⊄ "Emergency Dental Service"       → normal
  for (const t of sv) {
    if (!catSet.has(t)) return false;
  }
  return true;
}

export function GbpServicesEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (text: string) => void;
}) {
  // Parse the text into an ordered category→services map. We re-parse from the
  // incoming value so external changes (e.g. the Parse button) flow through.
  // keepEmptyCategories: true so removing the last service from a category does
  // NOT delete the category section (fix266) — the user can re-add services.
  const map = useMemo(
    () => parseGbpServicesText(value, { keepEmptyCategories: true }),
    [value]
  );

  // Real categories = everything except the reserved Unassigned bucket.
  const categories = Object.keys(map).filter((c) => c !== UNASSIGNED);
  const unassigned = map[UNASSIGNED] ?? [];

  // Flat list of every service across all categories (incl. unassigned),
  // de-duplicated case-insensitively, in first-seen order.
  const flatServices = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const list of Object.values(map)) {
      for (const s of list) {
        const key = s.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(s);
      }
    }
    return out;
  }, [map]);

  const [newService, setNewService] = useState<Record<string, string>>({});
  const [newCategory, setNewCategory] = useState("");
  const [newFlatService, setNewFlatService] = useState("");
  // chunk3z-fix534: which service chip is in inline-edit mode, plus its draft
  // text. null = nothing being edited.
  const [editing, setEditing] = useState<
    { category: string; idx: number; value: string } | null
  >(null);

  function commit(next: Record<string, string[]>) {
    onChange(serialize(next));
  }

  // ---- Flat GBP Services list --------------------------------------------

  function removeFlatService(service: string) {
    // Remove this service from EVERY category it appears in.
    const needle = service.trim().toLowerCase();
    const next: Record<string, string[]> = {};
    for (const [cat, list] of Object.entries(map)) {
      const filtered = list.filter((s) => s.trim().toLowerCase() !== needle);
      if (filtered.length > 0 || cat !== UNASSIGNED) next[cat] = filtered;
    }
    commit(next);
  }

  function addFlatService() {
    const val = newFlatService.trim();
    if (!val) return;
    if (findServiceCategory(map, val)) {
      setNewFlatService("");
      return; // already exists somewhere
    }
    // New services land in the Unassigned bucket until assigned to a category.
    const next: Record<string, string[]> = { ...map };
    next[UNASSIGNED] = [...(next[UNASSIGNED] ?? []), val];
    commit(next);
    setNewFlatService("");
  }

  // ---- Services by category ----------------------------------------------

  function removeService(category: string, idx: number) {
    const next: Record<string, string[]> = { ...map };
    next[category] = next[category].filter((_, i) => i !== idx);
    commit(next);
  }

  // chunk3z-fix534: rename a service in place (double-click a chip to edit).
  // Because this editor is a controlled view onto the single canonical
  // category→services text (commit → onChange → serialize), renaming here
  // propagates everywhere else in the app that reads that same field — no
  // separate copies to keep in sync.
  function renameService(category: string, idx: number, rawValue: string) {
    const value = rawValue.trim();
    const current = (map[category] ?? [])[idx] ?? "";
    // No-op or empty → cancel without mutating (empty is treated as "keep",
    // not "delete" — deletion is the × button's job).
    if (!value || value === current) {
      setEditing(null);
      return;
    }
    const next: Record<string, string[]> = { ...map };
    next[category] = (next[category] ?? []).map((s, i) => (i === idx ? value : s));
    commit(next);
    setEditing(null);
  }

  function addService(category: string) {
    const val = (newService[category] ?? "").trim();
    if (!val) return;
    const next: Record<string, string[]> = { ...map };
    // If the service is currently unassigned, move it out of Unassigned.
    if (next[UNASSIGNED]) {
      next[UNASSIGNED] = next[UNASSIGNED].filter(
        (s) => s.trim().toLowerCase() !== val.toLowerCase()
      );
      if (next[UNASSIGNED].length === 0) delete next[UNASSIGNED];
    }
    next[category] = [...(next[category] ?? []), val];
    commit(next);
    setNewService((p) => ({ ...p, [category]: "" }));
  }

  function deleteCategory(category: string) {
    const next: Record<string, string[]> = { ...map };
    delete next[category];
    commit(next);
  }

  function addCategory() {
    const val = newCategory.trim();
    if (!val) return;
    if (map[val] || val.toLowerCase() === UNASSIGNED.toLowerCase()) return;
    commit({ ...map, [val]: [] });
    setNewCategory("");
  }

  // ---- Assign an unassigned service to a category ------------------------

  function assignUnassigned(service: string, category: string) {
    const needle = service.trim().toLowerCase();
    const next: Record<string, string[]> = { ...map };
    next[UNASSIGNED] = (next[UNASSIGNED] ?? []).filter(
      (s) => s.trim().toLowerCase() !== needle
    );
    if (next[UNASSIGNED].length === 0) delete next[UNASSIGNED];
    next[category] = [...(next[category] ?? []), service];
    commit(next);
  }

  function removeUnassigned(idx: number) {
    const next: Record<string, string[]> = { ...map };
    next[UNASSIGNED] = (next[UNASSIGNED] ?? []).filter((_, i) => i !== idx);
    if (next[UNASSIGNED].length === 0) delete next[UNASSIGNED];
    commit(next);
  }

  return (
    <div className="space-y-5">
      {/* ---- Flat GBP Services list ---------------------------------- */}
      <div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {flatServices.length === 0 && (
            <span className="text-text-subtle text-xs italic">
              No services yet. Add one below, or paste your GBP services above
              and click Parse.
            </span>
          )}
          {flatServices.map((svc, i) => (
            <span
              key={`flat-${svc}-${i}`}
              className="inline-flex items-center gap-1 bg-bg-card border border-border rounded-full pl-2.5 pr-1.5 py-1 text-xs text-text"
            >
              {svc}
              <button
                type="button"
                onClick={() => removeFlatService(svc)}
                className="text-text-subtle hover:text-danger leading-none"
                aria-label={`Remove ${svc}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newFlatService}
            onChange={(e) => setNewFlatService(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addFlatService();
              }
            }}
            placeholder="Add service…"
            className="flex-1 bg-bg-card border border-border rounded-md px-2.5 py-1.5 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-accent"
          />
          <button
            type="button"
            onClick={addFlatService}
            className="bg-accent text-white text-sm font-medium rounded-md px-3 py-1.5 hover:opacity-90"
          >
            Add
          </button>
        </div>
        <p className="text-text-subtle text-xs mt-1.5">
          The flat list of all services. Editing chips here does NOT update
          category assignments — use the grouped view below to control which
          services belong to each category.
        </p>
      </div>

      {/* ---- Services by Category ------------------------------------ */}
      <div>
        <p className="text-text font-semibold text-sm mb-2">
          Services by Category
        </p>
        <div className="space-y-3">
          {categories.length === 0 && (
            <p className="text-text-subtle text-sm">
              No categories yet. Add one below to start grouping services.
            </p>
          )}

          {categories.map((category) => (
            <div
              key={category}
              className="bg-bg-subtle border border-border rounded-lg p-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-text font-semibold text-sm">
                    {category}
                  </span>
                  <span className="text-text-subtle text-xs">
                    {map[category].length} service
                    {map[category].length === 1 ? "" : "s"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => deleteCategory(category)}
                  className="text-text-subtle hover:text-danger text-xs"
                >
                  Delete
                </button>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-2">
                {map[category].map((svc, i) => {
                  const mirrors = serviceMirrorsCategory(svc, category);
                  const isEditing =
                    editing?.category === category && editing?.idx === i;
                  if (isEditing) {
                    return (
                      <input
                        key={`edit-${category}-${i}`}
                        autoFocus
                        value={editing.value}
                        onChange={(e) =>
                          setEditing({ category, idx: i, value: e.target.value })
                        }
                        onBlur={() => renameService(category, i, editing.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            renameService(category, i, editing.value);
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            setEditing(null);
                          }
                        }}
                        className="inline-flex rounded-full border border-accent bg-bg-card pl-2.5 pr-2 py-1 text-xs text-text outline-none focus:ring-1 focus:ring-accent"
                        style={{ width: `${Math.max(editing.value.length + 2, 8)}ch` }}
                        aria-label={`Edit ${svc}`}
                      />
                    );
                  }
                  return (
                    <span
                      key={`${svc}-${i}`}
                      onDoubleClick={() =>
                        setEditing({ category, idx: i, value: svc })
                      }
                      className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1.5 py-1 text-xs border cursor-text select-none ${
                        mirrors
                          ? "bg-danger-bg border-danger-border text-danger"
                          : "bg-bg-card border-border text-text"
                      }`}
                      title={
                        mirrors
                          ? "This service just repeats its category — categories shouldn't be listed as services. Consider removing it. (Double-click to edit.)"
                          : "Double-click to edit"
                      }
                    >
                      {svc}
                      <button
                        type="button"
                        onClick={() => removeService(category, i)}
                        className={`leading-none ${
                          mirrors
                            ? "text-danger/70 hover:text-danger"
                            : "text-text-subtle hover:text-danger"
                        }`}
                        aria-label={`Remove ${svc}`}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                {map[category].length === 0 && (
                  <span className="text-text-subtle text-xs italic">
                    No services in this category yet.
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newService[category] ?? ""}
                  onChange={(e) =>
                    setNewService((p) => ({ ...p, [category]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addService(category);
                    }
                  }}
                  placeholder="Add service…"
                  className="flex-1 bg-bg-card border border-border rounded-md px-2.5 py-1 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-accent"
                />
                <button
                  type="button"
                  onClick={() => addService(category)}
                  className="text-accent text-xs font-medium px-2 hover:underline"
                >
                  Add
                </button>
              </div>
            </div>
          ))}

          {/* Add a new category */}
          <div className="flex gap-2 pt-1">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCategory();
                }
              }}
              placeholder="Add category (e.g. Cosmetic Dentist)…"
              className="flex-1 bg-bg-card border border-border rounded-md px-2.5 py-1.5 text-sm text-text placeholder:text-text-subtle focus:outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={addCategory}
              className="bg-accent text-white text-sm font-medium rounded-md px-3 py-1.5 hover:opacity-90"
            >
              Add Category
            </button>
          </div>
        </div>
      </div>

      {/* ---- Unassigned bucket --------------------------------------- */}
      {unassigned.length > 0 && (
        <div className="bg-bg-subtle border border-border rounded-lg p-3">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-text font-semibold text-sm">Unassigned</span>
            <span className="text-text-subtle text-xs">
              {unassigned.length} service{unassigned.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            {unassigned.map((svc, i) => (
              <div
                key={`unassigned-${svc}-${i}`}
                className="flex items-center gap-2"
              >
                <span className="inline-flex items-center gap-1 bg-bg-card border border-border rounded-full pl-2.5 pr-1.5 py-1 text-xs text-text">
                  {svc}
                  <button
                    type="button"
                    onClick={() => removeUnassigned(i)}
                    className="text-text-subtle hover:text-danger leading-none"
                    aria-label={`Remove ${svc}`}
                  >
                    ×
                  </button>
                </span>
                {categories.length > 0 && (
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) assignUnassigned(svc, e.target.value);
                    }}
                    className="bg-bg-card border border-border rounded-md px-2 py-1 text-xs text-text-muted focus:outline-none focus:border-accent"
                  >
                    <option value="">Assign to…</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
          <p className="text-text-subtle text-xs mt-2">
            Unassigned services don't appear on category pages. Assign them to a
            category above to include them.
          </p>
        </div>
      )}
    </div>
  );
}
